import ArrowAsc from '@svg/arrow-asc.svg?react';
import ArrowDesc from '@svg/arrow-desc.svg?react';
import { Skeleton } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { AutoSizer, Column, Index, IndexRange, Table } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { Column as ColumnConfig } from '../../../../model/table';
import { SortDirection } from '@src/utils/enums';
import { IconType } from '../../../shared/custom-icon/custom-icon';
import { Highlight } from '../../../shared/highlight/highlight';
import Loader from '../../../shared/loader/loader';
import { NoResult } from '../../../shared/no-result/no-result';
import './virtualized-table.scss';

const LoadingMoreDataLoader = () => <Skeleton.Input active size={'small'} />;

interface VirtualizedTableProps<T> {
    columnsConfig: ColumnConfig<T>[];
    content: T[];
    searchText?: string;
    isInitialState: boolean;
    isLoading: boolean;
    sortInBe?: <K extends keyof T> (sortBy: K, sortDirection: SortDirection) => Promise<void>;
    onRowsRendered?: (params: IndexRange) => void;
    innerRef?: (registeredChild: any) => void;
    emptyStateComp: ReactNode;
    noResultIcon: IconType;
    rowCount?: number;
    newSearch?: number;
}

export const VirtualizedTable = <T,>({ columnsConfig, content, searchText, isInitialState, isLoading, sortInBe,
    onRowsRendered,
    innerRef,
    emptyStateComp,
    noResultIcon,
    rowCount, newSearch
}: VirtualizedTableProps<T>) => {

    const [sortedContent, setSortedContent] = useState([...content]);
    const [sortDirection, setSortDirection] = useState<Record<keyof T, SortDirection>>({} as Record<keyof T, SortDirection>);

    useEffect(() => {
        setSortedContent(content);
    }, [content]);

    const rowGetter = ({ index }: { index: number }) => sortedContent[index] || {};

    function sortInFE<K extends keyof T>(columnAccessor: K, newSortDirection: SortDirection) {
        const sortedData = [...sortedContent].sort((a, b) => {
            const valueA = a[columnAccessor];
            const valueB = b[columnAccessor];
            if (valueA < valueB) {
                return newSortDirection === SortDirection.ASC ? -1 : 1;
            }
            if (valueA > valueB) {
                return newSortDirection === SortDirection.ASC ? 1 : -1;
            }
            return 0;
        });
        setSortedContent(sortedData);
        setSortDirection({ ...sortDirection, [columnAccessor]: newSortDirection });
    }

    const sortData = async <K extends keyof T>(columnAccessor: K) => {
        const currentSortDirection = sortDirection[columnAccessor] || SortDirection.ASC;
        const newSortDirection =
            currentSortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
        if (sortInBe) {
            await sortInBe(columnAccessor, newSortDirection);
            setSortDirection({ [columnAccessor]: newSortDirection } as Record<keyof T, SortDirection>);
        } else {
            sortInFE(columnAccessor, newSortDirection);
        }
    };

    const renderArrowIcon = (columnAccessor: keyof T) => {
        const direction = sortDirection[columnAccessor];
        if (direction === SortDirection.ASC) {
            return <ArrowAsc />;
        } else if (direction === SortDirection.DESC) {
            return <ArrowDesc />;
        }
    };

    const noRowsDisplay = useMemo(() =>
        isInitialState ? emptyStateComp :
            !sortedContent.length && <NoResult iconType={noResultIcon} subTitle={'Change the filters and try again.'} />
        , [isInitialState, sortedContent.length]);

    const renderTableState = () => {
        if (isLoading) {
            return <Loader type={'component'} />;
        }
        return noRowsDisplay;
    };

    const totalColumnWidths = columnsConfig.reduce(
        (accumulator, column) => accumulator + (column.width as number),
        0
    );

    const cellRenderer = (rowData: any, column: any) => {
        if (rowData === null || isEmpty(rowData))
            return <LoadingMoreDataLoader />;

        return <Highlight searchText={searchText}>
            {column.getDisplayElement ? column.getDisplayElement(column, rowData) :
                <div data-testid={column.accessor}>{rowData[column.accessor]}</div>}
        </Highlight>;
    };

    // Accept ReactNode and handle undefined label
    const renderHeaderCell = (label: React.ReactNode, accessor: keyof T) => (
        <button type="button" className={'header-cell'} onClick={() => sortData(accessor)}>
            {label ?? ''}
            {renderArrowIcon(accessor)}
        </button>
    );

    return (
        <div className="virtualized-table"><AutoSizer>
            {({ width, height }) => (
                <div>
                    <div className="table-container">
                        <Table
                            width={Math.max(width, totalColumnWidths)}
                            height={height || 200}
                            headerHeight={60}
                            rowHeight={65}
                            key={newSearch}
                            rowCount={rowCount ?? sortedContent.length}
                            rowGetter={rowGetter}
                            onRowsRendered={onRowsRendered}
                            rowClassName={((info: Index) => `row-${info.index}`)}
                            ref={innerRef}>
                            {columnsConfig.map(column => (
                                <Column
                                    key={column.accessor}
                                    dataKey={column.accessor}
                                    label={column.header}
                                    width={column.width as number}
                                    cellRenderer={({ rowData }) => cellRenderer(rowData, column)}
                                    headerRenderer={({ label }) => renderHeaderCell(label, column.accessor)}
                                />
                            ))}
                        </Table>
                    </div>
                    {renderTableState()}
                </div>
            )}
        </AutoSizer></div>
    );
};